import api from './api';
import { getTodayLocal } from '../utils/dateUtils';

// ---------------------------------------------------------------------------
// Icon map — add entries as your DB grows
// ---------------------------------------------------------------------------
const DEPT_ICONS = {
  'all':                    '🏥',
  'general-medicine':       '👨‍⚕️',
  'general-physician':      '🩺',
  'paediatrics':            '👶',
  'obstetrics-gynaecology': '🤰',
  'diabetology':            '🩸',
  'cardiology':             '❤️',
  'pulmonary-medicine':     '🫁',
  'orthopaedics':           '🦴',
  'dermatology':            '🧴',
  'infectious-disease':     '🦠',
  'allergy-immunology':     '🤧',
  'respiratory-medicine':   '😷',
  'tb-chest':               '🫀',
};

export const getDepartments = async () => {
  const { data } = await api.get('/consultations/departments');
  const depts = (data || []).map(d => ({
    id:   d.id,
    label: d.label,
    icon: DEPT_ICONS[d.id] ?? '🏥',
  }));
  return [{ id: 'all', label: 'All Departments', icon: '🏥' }, ...depts];
};

// Keep for any existing imports that reference DEPARTMENTS
export const DEPARTMENTS = [];

// ---------------------------------------------------------------------------
// Shape adapter
// ---------------------------------------------------------------------------
const adapt = (doctor) => ({
  ...doctor,
  yearsExperience: doctor.yearsExperience ?? doctor.experience ?? 0,
  availableWalkIn: doctor.availableWalkIn ?? doctor.availableOffline ?? false,
  departmentId:    doctor.department,
  photoUrl: doctor.photoUrl && doctor.photoUrl.trim()
    ? doctor.photoUrl
    : generateAvatar(doctor.name),
});

const generateAvatar = (name = '') => {
  const seed = name.replace(/\s+/g, '+');
  return `https://ui-avatars.com/api/?name=${seed}&background=dbeafe&color=1d4ed8&size=160&font-size=0.4&bold=true`;
};

// ---------------------------------------------------------------------------
// GET /api/consultations/doctors
// ---------------------------------------------------------------------------
export const getDoctors = async ({ mode = 'online', department = 'all', pincode, query, lat, lon } = {}) => {
  const params = { mode };
  if (department && department !== 'all') params.department = department;
  if (pincode && pincode.trim()) params.pincode = pincode.trim();
  if (lat != null) params.lat = lat;
  if (lon != null) params.lon = lon;

  const { data } = await api.get('/consultations/doctors', { params });

  let doctors = (data || []).map(adapt);

  if (query && query.trim()) {
    const q = query.toLowerCase().trim();
    doctors = doctors.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.departmentLabel || '').toLowerCase().includes(q) ||
        (d.qualifications  || '').toLowerCase().includes(q)
    );
  }

  return doctors;
};

// ---------------------------------------------------------------------------
// GET /api/consultations/doctors/:id?mode=online|walk-in
// ---------------------------------------------------------------------------
export const getDoctorById = async (id, mode = 'online') => {
  const { data } = await api.get(`/consultations/doctors/${id}`, { params: { mode } });
  return adapt(data);
};

// ---------------------------------------------------------------------------
// GET /api/consultations/doctors/:id/slots?date=YYYY-MM-DD&mode=online|walk-in
// ---------------------------------------------------------------------------
export const getSlots = async (doctorId, dateISO, mode = 'online') => {
  const { data } = await api.get(`/consultations/doctors/${doctorId}/slots`, {
    params: { date: dateISO, mode },
  });
  return data || [];
};

// ---------------------------------------------------------------------------
// POST /api/consultations/upload-medical-report
// ---------------------------------------------------------------------------
export const uploadMedicalReport = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await api.post('/consultations/upload-medical-report', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return data; // { s3Key, fileUrl, fileName }
};

// ---------------------------------------------------------------------------
// POST /api/consultations/appointments
// ---------------------------------------------------------------------------
export const bookAppointment = async ({
  doctorId,
  slotId,
  department,
  appointmentDate,
  mode,
  patient,
  isSecondOpinion,
  reportFile,
  payment, // ← NEW: { razorpayOrderId, razorpayPaymentId, razorpaySignature } — omitted for free bookings
}) => {
  // For walk-in: extract date from slotId. For online auto-assign: use passed appointmentDate.
  const resolvedDate = appointmentDate
    || (slotId && doctorId ? extractDateFromSlotId(slotId, doctorId) : getTodayLocal());

  let previousReportsPath = null;
  if (isSecondOpinion && reportFile) {
    try {
      const uploadResponse = await uploadMedicalReport(reportFile);
      previousReportsPath = uploadResponse.s3Key;
    } catch {
      throw new Error('Failed to upload medical report. Please try again.');
    }
  }

  const payload = {
    doctorId:            doctorId   || null,
    slotId:              slotId     || null,
    department:          department || null,
    mode,
    appointmentDate:     resolvedDate,
    name:                patient.name,
    phone:               patient.phone,
    email:               patient.email  || null,
    age:                 parseInt(patient.age, 10),
    gender:              patient.gender,
    relation:            patient.relation || null,
    reason:              patient.currentComplaint || null,
    previousHealthHistory: patient.previousHealthHistory || null,
    isSecondOpinion:     isSecondOpinion || false,
    previousReportsPath,
    // ── NEW: Razorpay payment proof, verified again by the backend ──────
    razorpayOrderId:     payment?.razorpayOrderId     || null,
    razorpayPaymentId:   payment?.razorpayPaymentId   || null,
    razorpaySignature:   payment?.razorpaySignature   || null,
  };

  const { data } = await api.post('/consultations/appointments', payload);

  return {
    ...data,
    confirmationId: data.confirmationId
      || `APT-${data.id?.toString().slice(0, 6).toUpperCase()}`,
    status: data.status || 'CONFIRMED',
  };
};

// ---------------------------------------------------------------------------
// GET /api/consultations/appointments/my
// ---------------------------------------------------------------------------
export const getMyAppointments = async () => {
  const { data } = await api.get('/consultations/appointments/my');
  return data || [];
};

// ---------------------------------------------------------------------------
// Helper — extracts YYYY-MM-DD from slotId
// slotId format: `${doctorId}-${dateISO}-${time}`
// e.g. "dc000001-0000-0000-0000-000000000001-2026-05-15-09:00"
// ---------------------------------------------------------------------------
const extractDateFromSlotId = (slotId, doctorId) => {
  const withoutId = slotId.startsWith(doctorId)
    ? slotId.slice(doctorId.length + 1)
    : slotId;

  const match = withoutId.match(/(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];

  return getTodayLocal();
};